/**
 * Soft purple glow (Figma "Purple Glow", #7E22CE). Rendered as a radial gradient
 * — NOT a `filter: blur()` — because large blur filters intermittently fail to
 * composite on first paint (the "fixes itself when you open devtools" bug).
 *
 * The gradient uses many finely-spaced stops with a gently decreasing slope and a
 * long tail that reaches full transparency only at the box edge — this avoids the
 * hard elliptical "ring" / recognisable shape that a short, steep falloff shows
 * on large screens. A faint dithering layer (SVG noise, masked by the same
 * gradient) breaks up the 8-bit colour banding that otherwise appears as
 * concentric rings across the many pixels a glow spans on a wide display.
 */

// Shared falloff so desktop glows and the mobile ambient glows match exactly.
// `intensity` (0–1) scales the peak opacity (default tuned to read as ambient
// light rather than a defined blob, even on 2560px+ screens).
export function glowGradient(intensity = 1) {
  const a = (v: number) => `rgba(126,34,206,${(v * intensity).toFixed(3)})`;
  return `radial-gradient(closest-side,
    ${a(0.4)} 0%,
    ${a(0.34)} 14%,
    ${a(0.26)} 28%,
    ${a(0.18)} 42%,
    ${a(0.11)} 56%,
    ${a(0.06)} 70%,
    ${a(0.025)} 84%,
    ${a(0)} 100%)`;
}

// Tileable fractal-noise used purely to dither the gradient and kill banding.
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

export default function Glow({
  left,
  top,
  width,
  ratio,
  intensity = 1,
}: {
  left: string;
  top: string;
  width: string;
  ratio: string;
  intensity?: number;
}) {
  const gradient = glowGradient(intensity);
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left, top, width, aspectRatio: ratio }}
    >
      <div className="absolute left-1/2 top-1/2 h-[165%] w-[165%] -translate-x-1/2 -translate-y-1/2">
        <div className="absolute inset-0" style={{ background: gradient }} />
        {/* dithering: noise shown only where the glow is (masked by the gradient) */}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{
            backgroundImage: NOISE,
            WebkitMaskImage: gradient,
            maskImage: gradient,
          }}
        />
      </div>
    </div>
  );
}
